import { OptimizelyProduct, Target } from '../../types/DataSyncItem';
import { DataSyncObjectsStore } from './DataSyncObjectsStore';
import DataSyncCgObjectsStore from './DataSyncCgObjectsStore';
import DataSyncCmpObjectsStore from './DataSyncCmpObjectsStore';
import DataSyncHubAppObjectsStore from './DataSyncHubAppObjectsStore';
import DataSyncOdpObjectsStore from './DataSyncOdpObjectsStore';
import DataSyncCrObjectsStore from './DataSyncCrObjectsStore';

/**
 * Factory class for creating DataSyncObjectsStore instances based on product type
 */
export class DataSyncObjectStoreFactory {

  /**
   * Creates or returns a cached instance of a DataSyncObjectsStore for the given product type
   *
   * @param product The OptimizelyProduct to create a store for
   * @param target The Target (SOURCE or DESTINATION) for which the store is created
   * @param instanceId Optional instance ID for the product, used for specific instances like CMP or Hub App
   * @returns A DataSyncObjectsStore instance appropriate for the product type
   */
  public static createStore(
    product: OptimizelyProduct,
    target: Target,
    instanceId?: string
  ): DataSyncObjectsStore | null {

    if (!product) {
      return null;
    }

    const cacheKey = this.generateCacheKey(product, target, instanceId);

    const cachedStore = this.storeCache.get(cacheKey);
    if (cachedStore) {
      return cachedStore;
    }

    let store: DataSyncObjectsStore;

    switch (product) {
      case OptimizelyProduct.CGR:
        store = new DataSyncCgObjectsStore(target, instanceId);
        break;
      case OptimizelyProduct.CMP:
        store = new DataSyncCmpObjectsStore(target, instanceId);
        break;
      case OptimizelyProduct.CR:
        store = new DataSyncCrObjectsStore(target, instanceId);
        break;
      case OptimizelyProduct.HUB:
        store = new DataSyncOdpObjectsStore(target);
        break;
      case OptimizelyProduct.HUB_APP:
        store = new DataSyncHubAppObjectsStore(target, instanceId);
        break;
      default:
        throw new Error(`Unsupported product type: ${product}`);
    }

    this.storeCache.set(cacheKey, store);

    return store;
  }
  private static storeCache: Map<string, DataSyncObjectsStore> = new Map();

  private static generateCacheKey(product: OptimizelyProduct, target: Target, instanceId?: string): string {
    return `${product}:${target}:${instanceId || 'null'}`;
  }
}
